// /app/frontend/src/components/admin/InstallerRouteMap.jsx
// Display daily route for installers with Google Maps integration

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  MapPin, Navigation, Clock, Phone, User, ExternalLink,
  Loader2, ChevronDown, ChevronUp, Route, Car, CheckCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InstallerRouteMap({ date, showAdminView = false, installerId = null }) {
  const [routeData, setRouteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedStop, setExpandedStop] = useState(null);

  const token = localStorage.getItem('apollo_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchRoute();
  }, [date, installerId]);

  const fetchRoute = async () => {
    setLoading(true);
    try {
      let endpoint = `${API}/api/my-schedule/route`;
      if (showAdminView && installerId) {
        endpoint = `${API}/api/admin/installer/${installerId}/route`;
      }
      
      const params = date ? `?date=${date}` : '';
      const response = await axios.get(`${endpoint}${params}`, { headers });
      setRouteData(response.data);
    } catch (error) {
      console.error('Failed to fetch route:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'scheduled': 'bg-blue-100 text-blue-700',
      'en_route': 'bg-amber-100 text-amber-700',
      'in_progress': 'bg-green-100 text-green-700 animate-pulse',
      'completed': 'bg-green-500 text-white'
    };
    return colors[status] || colors['scheduled'];
  };

  const openInMaps = (address) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=driving`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </CardContent>
      </Card>
    );
  }

  if (!routeData || routeData.total_jobs === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Route className="h-5 w-5" />
            Today's Route
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-8 text-gray-500">
          <MapPin className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No jobs scheduled for this day</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Route className="h-5 w-5 text-amber-600" />
              {showAdminView ? `${routeData.installer_name}'s Route` : "Today's Route"}
            </CardTitle>
            <CardDescription>
              {routeData.total_jobs} stop{routeData.total_jobs !== 1 ? 's' : ''} on {routeData.date}
            </CardDescription>
          </div>
          {routeData.route_url && (
            <Button
              onClick={() => window.open(routeData.route_url, '_blank')}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="open-route-map-btn"
            >
              <Navigation className="h-4 w-4 mr-2" />
              Open in Maps
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Route Timeline */}
        <div className="relative">
          {/* Vertical line connecting stops */}
          <div className="absolute left-5 top-8 bottom-8 w-0.5 bg-gray-200"></div>
          
          <div className="space-y-4">
            {routeData.stops.map((stop, index) => (
              <div key={stop.event_id} className="relative">
                {/* Stop marker */}
                <div className="flex items-start gap-4">
                  <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                    stop.status === 'completed' ? 'bg-green-500' :
                    stop.status === 'in_progress' ? 'bg-amber-500' :
                    'bg-gray-400'
                  }`}>
                    {stop.status === 'completed' ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      stop.order
                    )}
                  </div>
                  
                  {/* Stop details */}
                  <div 
                    className="flex-1 bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setExpandedStop(expandedStop === stop.event_id ? null : stop.event_id)}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold">{stop.title || stop.event_type}</h4>
                          <Badge className={getStatusColor(stop.status)}>
                            {stop.status?.replace('_', ' ')}
                          </Badge>
                        </div>
                        {stop.time && (
                          <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                            <Clock className="h-3 w-3" />
                            {stop.time}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openInMaps(stop.address);
                          }}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Car className="h-4 w-4" />
                        </Button>
                        {expandedStop === stop.event_id ? (
                          <ChevronUp className="h-4 w-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </div>
                    
                    {/* Address */}
                    <p className="text-sm text-gray-500 flex items-start gap-1 mt-2">
                      <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      {stop.address}
                    </p>
                    
                    {/* Expanded details */}
                    {expandedStop === stop.event_id && (
                      <div className="mt-4 pt-4 border-t space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <User className="h-4 w-4 text-gray-400" />
                          <span>{stop.customer_name}</span>
                        </div>
                        {stop.customer_phone && (
                          <a 
                            href={`tel:${stop.customer_phone}`}
                            className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Phone className="h-4 w-4" />
                            {stop.customer_phone}
                          </a>
                        )}
                        {stop.notes && (
                          <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                            <strong>Notes:</strong> {stop.notes}
                          </p>
                        )}
                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openInMaps(stop.address);
                            }}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <Navigation className="h-3 w-3 mr-1" />
                            Navigate
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Drive time indicator between stops */}
                {index < routeData.stops.length - 1 && (
                  <div className="ml-5 pl-4 py-2 text-xs text-gray-400 flex items-center gap-1">
                    <Car className="h-3 w-3" />
                    Drive to next stop
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Summary */}
        <div className="mt-6 pt-4 border-t flex items-center justify-between text-sm text-gray-600">
          <span>{routeData.stops.filter(s => s.status === 'completed').length} of {routeData.total_jobs} completed</span>
          {routeData.route_url && (
            <a 
              href={routeData.route_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-blue-600 hover:underline"
            >
              View full route
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
